/**
 * ChainBuilder — builds the per-module `startWorldMatrix` for a Part.
 *
 * Chain composition (simplified — modules butt up edge-to-edge in this build):
 *   chain_world = Identity
 *   for each module_i:
 *     module_i.startWorldMatrix       = chain_world
 *     module_i.startPositionInPartMm  = cumulative
 *     module_i.trackLengthMm          = module_i.moduleLengthMm
 *     cumulative                     += trackLengthMm
 *     end_local = T( ΔX, -ΔY_canvas, 0 ) ⊗ R_z( -θ )
 *     chain_world = chain_world ⊗ end_local
 *
 * `chain_world` is a planar pose: 2D translation (X,Y) + Z-yaw. We carry it as
 * a tiny struct rather than a full 4×4 to keep the pure-math layer free of
 * Three.js. A Three.js helper in PathBuilder.ts converts to Matrix4 on demand.
 */

import { moduleEndDelta, sampleModulePath } from './pathMath.js';
import { getModuleEntry, type ModuleCatalogEntry } from './ModuleCatalog.js';
import type { ModuleEntry, ModuleType3D, Vec3 } from '../types.js';
import { XtsViewerErrorException } from '../types.js';

/** Planar pose (X,Y,yaw) in a 3D-RH-Z-up frame. Z is always 0 here. */
export interface PlanarPose {
  positionMm: Vec3;
  yawDeg: number;
}

export const IDENTITY_POSE: PlanarPose = Object.freeze({
  positionMm: [0, 0, 0] as Vec3,
  yawDeg: 0,
});

export interface BuiltModule {
  moduleType: ModuleType3D;
  catalog: ModuleCatalogEntry;
  /** Pose of the module-local origin (= path entry). */
  startWorldPose: PlanarPose;
  /** Distance along the part where this module starts (mm). */
  startPositionInPartMm: number;
  /** moduleLength (mm). Used for findModuleContaining. */
  trackLengthMm: number;
  /** Forwarded from `ModuleEntry.globalNumber` — index into the runtime
   *  `ModuleStatusStore` so renderers can subscribe per module. */
  globalNumber?: number;
  /** Forwarded from `ModuleEntry.status` so the renderer can light up
   *  the per-module drive-status overlay without an extra lookup. */
  status?: { warning?: boolean; error?: boolean };
}

export interface BuiltChain {
  modules: BuiltModule[];
  /** Σ moduleLength_i. */
  trackLengthMm: number;
  /** End-of-chain pose; useful for closure-checks. */
  endPose: PlanarPose;
}

const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;

function rotateXY(x: number, y: number, yawDeg: number): [number, number] {
  const c = Math.cos(yawDeg * DEG2RAD);
  const s = Math.sin(yawDeg * DEG2RAD);
  return [c * x - s * y, s * x + c * y];
}

/** Compose two planar poses: out = a ⊗ b (b in a's frame). */
function composePose(a: PlanarPose, b: PlanarPose): PlanarPose {
  const [bx, by] = rotateXY(b.positionMm[0], b.positionMm[1], a.yawDeg);
  return {
    positionMm: [
      a.positionMm[0] + bx,
      a.positionMm[1] + by,
      a.positionMm[2] + b.positionMm[2],
    ],
    yawDeg: a.yawDeg + b.yawDeg,
  };
}

/**
 * Build a chain from a part's module list.
 *
 * Throws XtsViewerErrorException with code `unknown-module-type` on lookup
 * miss.
 */
export function buildChain(modules: ReadonlyArray<ModuleEntry>): BuiltChain {
  let cumulative = 0;
  let chain: PlanarPose = IDENTITY_POSE;
  const out: BuiltModule[] = [];

  for (let i = 0; i < modules.length; i++) {
    const m = modules[i]!;
    const catalog = getModuleEntry(m.moduleType);
    if (!catalog) {
      throw new XtsViewerErrorException(
        'unknown-module-type',
        `Unknown moduleType "${m.moduleType}" at index ${i}`,
        { index: i, moduleType: m.moduleType },
      );
    }

    const trackLengthMm = catalog.moduleLengthMm;
    const startWorldPose = chain;

    out.push({
      moduleType: m.moduleType,
      catalog,
      status: m.status,
      globalNumber: m.globalNumber,
      startWorldPose,
      startPositionInPartMm: cumulative,
      trackLengthMm,
    });

    cumulative += trackLengthMm;

    // End-of-module local transform: T(ΔX, -ΔY_canvas, 0) ⊗ R_z(-θ).
    const end = moduleEndDelta(catalog);
    const endLocal: PlanarPose = {
      positionMm: end.deltaPositionMm,
      yawDeg: end.deltaYawDeg,
    };
    chain = composePose(chain, endLocal);
  }

  return {
    modules: out,
    trackLengthMm: cumulative,
    endPose: chain,
  };
}

/**
 * Locate the module containing `partPositionMm` along the chain.
 * First module with partPosition < startInPart + trackLength; at boundary
 * `partPosition == trackLength` the LAST module is returned with
 * `localPos = trackLength`.
 */
export function findModuleAt(
  chain: BuiltChain,
  partPositionMm: number,
): { module: BuiltModule; localPosMm: number } | null {
  if (chain.modules.length === 0) return null;
  const pos = Math.max(0, Math.min(partPositionMm, chain.trackLengthMm));
  if (pos >= chain.trackLengthMm) {
    const last = chain.modules[chain.modules.length - 1]!;
    return { module: last, localPosMm: last.trackLengthMm };
  }
  for (const m of chain.modules) {
    if (pos < m.startPositionInPartMm + m.trackLengthMm) {
      return { module: m, localPosMm: pos - m.startPositionInPartMm };
    }
  }
  // Invariant: the loop above must hit, because we already clamped `pos`
  // to [0, trackLengthMm) by the `pos >= chain.trackLengthMm` check.
  // Reaching here means the chain's per-module startPositionInPartMm /
  // trackLengthMm fields are inconsistent — surface it.
  throw new XtsViewerErrorException(
    'invalid-config',
    `findModuleAt: pos=${pos} did not land in any module; chain.modules is inconsistent.`,
    { pos, trackLengthMm: chain.trackLengthMm, moduleCount: chain.modules.length },
  );
}

/**
 * Mover world pose at `partPositionMm`.
 *
 * The module's startWorldPose is composed with the local path sample at
 * `localPosMm` along that module's path.
 */
export function moverWorldAt(
  chain: BuiltChain,
  partPositionMm: number,
): PlanarPose | null {
  const found = findModuleAt(chain, partPositionMm);
  if (!found) return null;
  const { module, localPosMm } = found;
  const sample = sampleModulePath(module.catalog, localPosMm);
  const local: PlanarPose = {
    positionMm: sample.positionMm,
    yawDeg: sample.yawDeg,
  };
  return composePose(module.startWorldPose, local);
}

/** Convenience: chain from full ModuleEntry[] returning trackLength alone. */
export function trackLengthOf(modules: ReadonlyArray<ModuleEntry>): number {
  return buildChain(modules).trackLengthMm;
}

export const _internals = { composePose, rotateXY, DEG2RAD, RAD2DEG };
